<template>
  <div v-if="!isLoading && !isBookmarksLoading" class="bookmarks">
    <template>
      <div v-show="bookmarks.length" class="bookmarks__container">
        <div class="dashboard__tab-sub-title bookmarks__title ">Bookmarks</div>
        <div
          v-for="course in bookmarks"
          :key="course.id"
          class="bookmarks__group"
        >
          <div class="bookmarks__group-course-label">
            {{ courseLabel(course) }}
          </div>
          <div
            v-for="video in course.videos"
            :key="video.video_id"
            class="bookmarks__group-container"
          >
            <div class="bookmarks__group-video-title">
              {{ videoTitle(video) }}
            </div>
            <div
              v-for="(bookmark, index) in video.bookmarks"
              class="bookmarks__group-wrapper"
              :key="bookmark.id"
            >
              <div class="bookmarks__group-path">
                <div
                  :class="[
                    'bookmarks__group-line',
                    'bookmarks__group-line--top',
                    isFirstVideo(index) && 'bookmarks__group-line--first',
                  ]"
                ></div>
                <BookmarkIcon
                  :class="[
                    'bookmarks__group-icon',
                    isFirstVideo(index) && 'bookmarks__group-icon--first',
                  ]"
                />
                <div
                  :class="[
                    'bookmarks__group-line',
                    'bookmarks__group-line--bottom',
                    isLastVideo(index, video) && 'bookmarks__group-line--last',
                  ]"
                ></div>
              </div>
              <div class="bookmarks__group-tile">
                <BookmarkTile
                  :bookmark="bookmark"
                  @delete-bookmark="deleteBookmark"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-show="!bookmarks.length" class="bookmarks__container">
        <div class="dashboard__tab-sub-title bookmarks__title ">
          No bookmarks
        </div>
      </div>
    </template>
  </div>
  <div v-else>
    <ActionLoader class="bookmarks__action-loader" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import BookmarkTile from '@/components/user-dashboard/learning/BookmarkTile.vue'
import { BookmarkIcon } from 'vue-feather-icons'
import ActionLoader from '@/components/shared/ActionLoader.vue'

export default {
  name: 'Bookmarks',
  components: {
    BookmarkTile,
    BookmarkIcon,
    ActionLoader,
  },
  props: {
    isLoading: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      bookmarks: [],
      isBookmarksLoading: true,
    }
  },
  computed: {
    ...mapState({
      rawBookmarks: state => state.content.currentBookmarks,
      courses: state => state.content.coursesForBookmarks,
      videos: state => state.content.videosForBookmarks,
    }),
  },
  watch: {
    rawBookmarks(newVal, oldVal) {
      if (newVal?.length > oldVal?.length) {
        this.prepareBookmarks()
      }
    },
  },
  methods: {
    async openBookmark(bookmark) {
      const { course, video, time } = bookmark
      this.$store.dispatch('content/setPlayingVideoId', video)
      this.$store.dispatch('content/setPlayingVideoTime', time)
      this.$router.push(this.$buildContentLink('course', course))
    },
    async deleteBookmark(id) {
      await this.$store.dispatch('content/removeBookmark', id)
      this.prepareBookmarks()
      this.$store.dispatch('content/getBookmarks')
    },
    async prepareBookmarks() {
      const bookmarks = []
      const courseIds = []
      const videoIds = []
      this.rawBookmarks.forEach(bookmark => {
        const {
          video,
          metadata: { course_id },
        } = bookmark
        if (video && video.length && course_id && course_id.length) {
          bookmarks.push(bookmark)
          if (!courseIds.includes(course_id)) {
            courseIds.push(course_id)
          }
          if (!videoIds.includes(video)) {
            videoIds.push(video)
          }
        }
      })
      const prepared = { bookmarks, courseIds, videoIds }
      if (prepared.courseIds.length) {
        await this.$store.dispatch(
          'content/getCoursesForBookmarks',
          prepared.courseIds
        )
      }
      if (prepared.videoIds.length) {
        await this.$store.dispatch(
          'content/getVideosForBookmarks',
          prepared.videoIds
        )
      }
      this.bookmarks = _.chain(this.rawBookmarks)
        .groupBy('metadata.course_id')
        .map((value, key) => {
          const course = this.courses.find(c => c.id === key)
          const videos = _.chain(value)
            .groupBy('video')
            .map(video => {
              const video_id = _.head(video).video
              const video_details = this.videos.find(v => v.id === video_id)
              const bookmark = _.map(video, o => {
                return {
                  ...o,
                  course: course,
                  video_details: video_details,
                }
              })
              const videoObj = {
                video_id: video_id,
                video_name: video_details?.name,
                video_details: video_details,
                bookmarks: bookmark,
              }
              return videoObj
            })
            .value()
          const courseObj = course
            ? {
                course_id: key,
                course_name: course.name,
                videos: videos,
              }
            : {
                course_id: '',
                course_name: 'Not Found',
                videos: [],
              }
          return courseObj
        })
        .value()
    },
    courseLabel(course) {
      return `Course: ${course?.course_name}`
    },
    videoTitle(video) {
      return video?.video_name
    },
    isFirstVideo(index) {
      return index === 0
    },
    isLastVideo(index, video) {
      return index === video.bookmarks.length - 1
    },
  },
  async created() {
    this.isBookmarksLoading = true
    if (this.rawBookmarks && this.rawBookmarks.length) {
      try {
        await this.prepareBookmarks()
      } finally {
        this.isBookmarksLoading = false
        this.$emit('bookmarks-loaded', true)
      }
    }
    this.isBookmarksLoading = false
    this.$store.dispatch('app/setIsComponentLoading', false)
  },
}
</script>

<style lang="scss" scoped>
.bookmarks {
  &__title {
    margin-bottom: 20px;
    @include mobile {
      margin-bottom: 30px;
    }
  }
  &__group {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 20px;
    &-course-label {
      font-size: rem(18);
      line-height: 24px;
      color: var(--white);
      font-weight: 500;
      margin-bottom: 20px;
    }
    &-video-title {
      font-size: rem(16);
      line-height: 24px;
      color: var(--light-blue-gray);
      margin-bottom: 15px;
    }
  }
  &__group {
    &-wrapper {
      display: flex;
    }
    &-container {
      margin-bottom: 25px;
    }
    &-path {
      position: relative;
      flex: 0 0 31px;
      color: var(--chetwode-blue);
      overflow: hidden;
      svg {
        width: 27.5px;
        height: 27.5px;
        margin-left: -4px;
      }
    }
    &-icon {
      &--first {
        margin-top: 7px;
      }
    }
    &-line {
      border-right: solid 1px var(--butterfly-bush);
      width: 10px;
      &--top {
        height: 10px;
      }
      &--bottom {
        height: 100%;
        margin-top: -7px;
      }
      &--last,
      &--first {
        display: none;
      }
    }
    &-tile {
      width: 100%;
    }
  }
  &__action-loader {
    text-align: center;
  }
}
::v-deep .bookmarks__loader {
  width: 100%;
  display: flex;
  justify-content: center;
}
</style>
